// Local Imports
import { exhaust, logger } from "CoreUtils";
import { layout, graphics as g, Bbox } from "GenAlphaModel";
import { schema, pdk_utils } from "GenAlphaSchema";
import * as style from "../../style";
import { IdMap } from "../../ids";
import { EntityKind } from "../../entity";
import { RectEntity } from "../../primitives";
import { Args, Entity } from "../../tree";
import type { LayoutRender } from "./render";
import { InstanceEntity } from "./instance";
import { RouteEntity } from "./route";
import { PinEntity } from "./pin";
import { DrawTo } from "../../draw";
// hasLayerVisibility no longer needed here - layer visibility is now
// checked via to.hiddenLayers from the DrawTo context
import { isViaLayer, drawViaCross } from "./via_pattern";
import { MIN_CULL_SCREEN_PX } from "../../draw/drawto";

// Assert that an Impl entity is present without a non-null assertion (`!`,
// lint-forbidden) or a throw. The overload is the typed boundary: callers see
// `ImplEntity` while the implementation returns the value untouched, so a
// genuinely-absent Impl still faults at the use site exactly as the old
// `this.implEntity!` did (see the createInstance/createRoute FIXMEs).
function present(impl: ImplEntity | undefined): ImplEntity;
function present(impl: unknown): unknown {
  return impl;
}

const MAX_HIER_LEVELS = 33; // Module scope setting for how many hierarchy levels to draw
const ALPHA = 0.5;

// Fallback colors for when PDK is not available or layer not found
// Extended palette for better visual distinction between unknown layers
const FALLBACK_COLORS = [
  style.Color.new(0, 255, 0, ALPHA), // Green
  style.Color.new(255, 0, 0, ALPHA), // Red
  style.Color.new(0, 0, 255, ALPHA), // Blue
  style.Color.new(0, 255, 255, ALPHA), // Cyan
  style.Color.new(255, 255, 0, ALPHA), // Yellow
  style.Color.new(255, 0, 255, ALPHA), // Magenta
  style.Color.new(255, 165, 0, ALPHA), // Orange
  style.Color.new(128, 0, 255, ALPHA), // Violet
  style.Color.new(255, 105, 180, ALPHA), // Hot Pink
  style.Color.new(0, 128, 255, ALPHA), // Dodger Blue
  style.Color.new(255, 215, 0, ALPHA), // Gold
  style.Color.new(255, 99, 71, ALPHA), // Tomato
  style.Color.new(147, 112, 219, ALPHA), // Medium Purple
];

// Special layer colors (not in PDK)
const SPECIAL_LAYERS = new Map([
  [2, style.Color.new(255, 0, 0, ALPHA)], // Axis layer - Red
  [10001, style.Color.new(255, 0, 255, ALPHA)], // Placement bbox - Purple
]);

// Track warned layers to avoid log spam
// Bounded by the finite number of layers in any technology (~1000 combinations max)
const warnedLayers = new Set<number>();

// Get layer color from PDK or fallback
// The encodedLayer parameter contains both layer and purpose encoded as:
// encodedLayer = layer * 65536 + purpose (see pdk_utils.encodeLayer)
// We decode it to get the layer number for PDK color lookup.
function getLayerColor(
  encodedLayer: number,
  pdk: schema.pdk.PDK | null
): style.Color {
  // Decode to get the actual layer number (purpose is not used for color lookup)
  const { layer: layerNumber, purpose: purposeNumber } =
    pdk_utils.decodeLayer(encodedLayer);

  // Check special layers first
  const specialColor = SPECIAL_LAYERS.get(layerNumber);
  if (specialColor) return specialColor;

  // Try to get color from PDK
  if (pdk) {
    const pdkLayer = pdk.layers.find((l) => l.number === layerNumber);
    if (pdkLayer?.color) {
      const { r, g: green, b, a } = pdkLayer.color;
      // PDK alpha is 0-255, convert to 0-1 for style.Color.new
      return style.Color.new(r, green, b, (a ?? 255) / 255);
    }

    // PDK exists but layer not found or has no color - warn once per layer
    if (!warnedLayers.has(layerNumber)) {
      logger.debug(
        `[LayerColor] Layer ${layerNumber} not found in PDK ${pdk.id} or has no color, using fallback`
      );

      // When importing from OA, if we cannot map the OA layer and purpose to a GenAlpha layer and purpose, we just store the OA layer and purpose numbers in the encodedLayer.
      // We print below to help with debugging!
      logger.debug(`encodedLayer: ${encodedLayer}`);
      logger.debug(`OA layerNumber: ${layerNumber}`);
      logger.debug(`OA purposeNumber: ${purposeNumber}`);

      warnedLayers.add(layerNumber);
    }
  } else {
    // No PDK available - warn once per layer
    if (!warnedLayers.has(layerNumber)) {
      warnedLayers.add(layerNumber);
      logger.debug(
        `[LayerColor] No PDK available for layer ${layerNumber}, using fallback`
      );
    }
  }

  // Fallback to color cycling based on layer number
  return FALLBACK_COLORS[layerNumber % FALLBACK_COLORS.length];
}

const styleFromColor = (color: style.Color): style.Style => ({
  shape: {
    fill: color,
    stroke: color,
    linewidth: 2,
    cap: "round",
    join: "round",
  },
  text: {
    family: style.THE_FONT,
    style: "normal",
    weight: 700,
    size: 16,
    fill: color,
    stroke: style.Special.None,
  },
});

const layerStyle = (layer: number, pdk: schema.pdk.PDK | null): style.Style =>
  styleFromColor(getLayerColor(layer, pdk));

export class LayoutEntity extends Entity<layout.Layout, LayoutRender> {
  //
  readonly entityKind = EntityKind.Layout;

  constructor(
    args: Args<layout.Layout, LayoutRender>,
    readonly hierLevel: number
  ) {
    super(args);
  }
  public absEntity = new AbstractEntity({
    data: this.data.abs,
    parent: this,
  });

  readonly makeImplEntity = (): ImplEntity | undefined => {
    if (!this.data.impl || this.hierLevel >= MAX_HIER_LEVELS) {
      return undefined;
    }
    return new ImplEntity(
      {
        data: this.data.impl,
        parent: this,
      },
      this.hierLevel
    );
  };
  public implEntity = this.makeImplEntity();

  override redraw = (to: DrawTo): unknown => {
    to.layer((to: DrawTo) => this.absEntity.redraw(to));
    to.layer((to: DrawTo) => this.implEntity?.redraw(to));
    return null;
  };

  toData = (): layout.Layout => {
    // Collect updated data from child drawers
    const abs = this.absEntity.toData();
    const impl = this.implEntity?.toData();

    // Create a new Layout instance with the updated data
    return new layout.Layout(
      this.data.ident,
      abs,
      impl,
      this.data.stack,
      this.data.properties
    );
  };

  //
  // FIXME: maybe skip all these, just send their calls to `routes.add` etc.
  //
  readonly createPin = (data: layout.LayoutPin): PinEntity => {
    return this.absEntity.pins.create(data);
  };
  readonly addPin = (drawer: PinEntity): void => {
    this.absEntity.pins.add(drawer);
  };
  readonly findPin = (id: number): PinEntity | undefined => {
    return this.absEntity.pins.get(id);
  };
  readonly createInstance = (data: layout.Instance): InstanceEntity => {
    return present(this.implEntity).instances.create(data); // FIXME: the assertion that we have an Impl!
  };
  readonly addInstance = (drawer: InstanceEntity): void => {
    this.implEntity?.instances.add(drawer);
  };
  readonly findInstance = (id: number): InstanceEntity | undefined => {
    return this.implEntity?.instances.get(id);
  };
  readonly createRoute = (data: layout.Route): RouteEntity => {
    return present(this.implEntity).routes.create(data); // FIXME: the assertion that we have an Impl!
  };
  readonly addRoute = (drawer: RouteEntity): void => {
    this.implEntity?.routes.add(drawer);
  };
  readonly findRoute = (id: number): RouteEntity | undefined => {
    return this.implEntity?.routes.get(id);
  };
  readonly createBlockage = (data: layout.Blockage): BlockageEntity => {
    return this.absEntity.blockages.create(data);
  };
  readonly addBlockage = (entity: BlockageEntity): void => {
    this.absEntity.blockages.add(entity);
  };
  readonly findBlockage = (id: number): BlockageEntity | undefined => {
    return this.absEntity.blockages.get(id);
  };

  // Find instance by name (e.g., "M1")
  readonly findInstanceByName = (name: string): InstanceEntity | undefined => {
    return this.implEntity?.findInstanceByName(name);
  };

  // Find all routes for a given net name
  readonly findRoutesByNet = (netName: string): RouteEntity[] => {
    return this.implEntity?.findRoutesByNet(netName) ?? [];
  };

  // Find layout-level pin by name (e.g., "VDD")
  readonly findPinByName = (name: string): PinEntity | undefined => {
    return this.absEntity.findPinByName(name);
  };

  // Find a child by kind and ID
  readonly find = (
    targetKind: "pin" | "route" | "instance" | "blockage",
    id: number
  ): InstanceEntity | RouteEntity | PinEntity | BlockageEntity | undefined => {
    switch (targetKind) {
      case "blockage":
        return this.findBlockage(id);
      case "instance":
        return this.findInstance(id);
      case "route":
        return this.findRoute(id);
      case "pin":
        return this.findPin(id);
      default:
        exhaust(targetKind);
    }
  };
}

const abstractBboxStyler: style.ShapeStyleTable = {
  //       light light    dark  dark
  //       no    hilited  no    hilited
  fill: style.Special.None,
  stroke: style.Color.red,
  linewidth: 2,
  cap: "round",
  join: "round",
};

// Blockage rendering (v2 redesign): neutral light fill + dashed
// layer-color border + diagonal hatch lines in the layer color so the
// blockage reads as a "keep-out" without colliding with any solid
// metal hue.
const BLOCKAGE_FILL_COLOR = style.Color.new(200, 200, 200, 0.15);
const BLOCKAGE_HATCH_ALPHA = 0.5;
const BLOCKAGE_BORDER_DASH = [6, 4];
const BLOCKAGE_BORDER_LINEWIDTH = 2;
const BLOCKAGE_HATCH_LINEWIDTH = 1;
// Number of diagonal hatch lines drawn across a square blockage; the
// count scales with the bbox aspect ratio so thin rectangles still get
// a few diagonals and fat rectangles don't get over-crowded.
const BLOCKAGE_HATCH_BASE_COUNT = 6;
const BLOCKAGE_HATCH_MAX_COUNT = 16;

// Generate diagonal lines clipped to `bbox`, each parameterized as
// y = x + c with c spaced uniformly across the diagonal range. Returns
// 2-point segment endpoints suitable for `to.path`.
function blockageHatchLines(bbox: schema.Bbox): Array<[g.Point, g.Point]> {
  const w = bbox.max.x - bbox.min.x;
  const h = bbox.max.y - bbox.min.y;
  if (w <= 0 || h <= 0) return [];
  const aspect = Math.max(w, h) / Math.max(1, Math.min(w, h));
  const count = Math.min(
    BLOCKAGE_HATCH_MAX_COUNT,
    Math.max(2, Math.round(BLOCKAGE_HATCH_BASE_COUNT * aspect))
  );
  // c (= y - x) ranges across the diagonal range.
  const cMin = bbox.min.y - bbox.max.x;
  const cMax = bbox.max.y - bbox.min.x;
  const step = (cMax - cMin) / (count + 1);
  const lines: Array<[g.Point, g.Point]> = [];
  for (let i = 1; i <= count; i++) {
    const c = cMin + i * step;
    // Intersect y = x + c with the four bbox edges; collect at most two
    // candidates that fall inside the bbox.
    const candidates: g.Point[] = [];
    const yAtMinX = bbox.min.x + c;
    if (yAtMinX >= bbox.min.y && yAtMinX <= bbox.max.y) {
      candidates.push({ x: bbox.min.x, y: yAtMinX });
    }
    const yAtMaxX = bbox.max.x + c;
    if (yAtMaxX >= bbox.min.y && yAtMaxX <= bbox.max.y) {
      candidates.push({ x: bbox.max.x, y: yAtMaxX });
    }
    const xAtMinY = bbox.min.y - c;
    if (xAtMinY > bbox.min.x && xAtMinY < bbox.max.x) {
      candidates.push({ x: xAtMinY, y: bbox.min.y });
    }
    const xAtMaxY = bbox.max.y - c;
    if (xAtMaxY > bbox.min.x && xAtMaxY < bbox.max.x) {
      candidates.push({ x: xAtMaxY, y: bbox.max.y });
    }
    if (candidates.length >= 2) {
      lines.push([candidates[0], candidates[1]]);
    }
  }
  return lines;
}

export class BlockageEntity extends Entity<layout.Blockage> {
  readonly entityKind = EntityKind.LayoutBlockage;

  toData = (): layout.Blockage => this.data;

  override redraw = (to: DrawTo): unknown => {
    if (!to.layoutDisplay?.blockages) return null;
    const { layer, bbox } = this.data;
    const encodedLayer = pdk_utils.encodeLayer(layer.layer, layer.purpose);
    // Skip if layer is hidden
    if (to.hiddenLayers.size > 0) {
      const { layer: layerNum } = pdk_utils.decodeLayer(encodedLayer);
      if (to.hiddenLayers.has(layerNum)) return null;
    }
    const baseColor = getLayerColor(encodedLayer, to.pdk);
    const hatchColor = style.Color.new(
      baseColor.r,
      baseColor.g,
      baseColor.b,
      BLOCKAGE_HATCH_ALPHA
    );

    // Neutral fill + dashed layer-color border.
    to.bbox(bbox, {
      fill: BLOCKAGE_FILL_COLOR,
      stroke: baseColor,
      linewidth: BLOCKAGE_BORDER_LINEWIDTH,
      lineDash: BLOCKAGE_BORDER_DASH,
      cap: "butt",
      join: "miter",
    });

    // Diagonal hatch in the layer color so the blocked layer is still
    // identifiable at a glance.
    const hatchStyle: style.ShapeStyle = {
      stroke: hatchColor,
      fill: style.Special.None,
      linewidth: BLOCKAGE_HATCH_LINEWIDTH,
      cap: "butt",
      join: "miter",
    };
    for (const [a, b] of blockageHatchLines(bbox)) {
      to.path(g.Path.fromPartial({ points: [a, b] }), hatchStyle);
    }
    return null;
  };
}

class Blockages extends IdMap<BlockageEntity> {
  constructor(private parent: AbstractEntity) {
    super();
  }
  get parentRef() {
    return this.parent;
  }
  readonly create = (data: layout.Blockage): BlockageEntity => {
    const entity = new BlockageEntity({ data, parent: this.parentRef });
    this.add(entity);
    return entity;
  };
  readonly createAll = (datas: Array<layout.Blockage>): this => {
    datas.forEach(this.create);
    return this;
  };
}

class Pins extends IdMap<PinEntity> {
  constructor(private parent: AbstractEntity) {
    super();
  }
  get parentRef() {
    return this.parent;
  }
  readonly create = (data: layout.LayoutPin): PinEntity => {
    const drawer = new PinEntity({ data, parent: this.parentRef });
    this.add(drawer);
    return drawer;
  };
  readonly createAll = (datas: Array<layout.LayoutPin>): this => {
    datas.forEach(this.create);
    return this;
  };
}

export class AbstractEntity extends Entity<layout.Abstract> {
  //
  readonly entityKind = EntityKind.LayoutAbstract;

  readonly pins = new Pins(this).createAll(this.data.pins); // Pins
  readonly blockages = new Blockages(this).createAll(this.data.blockages); // Blockages / Obstructions
  readonly bboxEntity = new RectEntity({
    data: g.Rectangle.new({ filled: true, bbox: this.data.bbox }),
    parent: this,
    table: abstractBboxStyler,
  });

  override redraw = (to: DrawTo): unknown => {
    return to.layer((to: DrawTo) => {
      this.blockages.forEach((blockage) => blockage.redraw(to));
      this.bboxEntity.redraw(to);
      this.pins.forEach((pin) => pin.redraw(to)); // Draw our pins
    });
  };

  // Find layout-level pin by name
  readonly findPinByName = (name: string): PinEntity | undefined => {
    for (const pin of this.pins.values()) {
      if (pin.data.name === name) return pin;
    }
    return undefined;
  };

  toData = (): layout.Abstract => ({
    bbox: this.data.bbox,
    outline: this.data.outline,
    pins: Array.from(this.pins.values()).map((pin) => pin.data),
    blockages: Array.from(this.blockages.values()).map((b) => b.toData()),
  });
}

class Instances extends IdMap<InstanceEntity> {
  constructor(private parent: ImplEntity) {
    super();
  }
  get parentRef() {
    return this.parent;
  }
  readonly create = (data: layout.Instance): InstanceEntity => {
    const drawer = new InstanceEntity(
      { data, parent: this.parentRef },
      this.parent.hierLevel + 1
    );
    this.add(drawer);
    return drawer;
  };
  readonly createAll = (datas: Array<layout.Instance>): this => {
    datas.forEach(this.create);
    return this;
  };
  readonly redraw = (to: DrawTo) => {
    this.forEach((inst) => inst.redraw(to));
  };
}

class Routes extends IdMap<RouteEntity> {
  constructor(private parent: ImplEntity) {
    super();
  }
  get parentRef() {
    return this.parent;
  }
  readonly create = (data: layout.Route): RouteEntity => {
    const drawer = new RouteEntity({ data, parent: this.parentRef });
    this.add(drawer);
    return drawer;
  };
  readonly createAll = (datas: Array<layout.Route>): this => {
    datas.forEach(this.create);
    return this;
  };
  readonly redraw = (to: DrawTo, localViewport: Bbox | null = null) => {
    // Deduplicate net labels: only the first route per net shows a label,
    // and only on its middle segment
    const labeledNets = new Set<string>();
    this.forEach((route) => {
      const isFirst = !labeledNets.has(route.data.net);
      if (isFirst) labeledNets.add(route.data.net);
      route.assignLabelVisibility(isFirst);
    });
    this.forEach((route) => route.redrawIn(to, localViewport));
  };
}

export class ImplEntity extends Entity<layout.Impl, LayoutRender> {
  //
  readonly entityKind = EntityKind.LayoutImpl;
  constructor(
    args: Args<layout.Impl, LayoutRender>,
    readonly hierLevel: number
  ) {
    super(args);
  }
  readonly instances = new Instances(this).createAll(this.data.instances); // Instances
  readonly routes = new Routes(this).createAll(this.data.routes); // Routes

  /**
   * Check if a layer is visible based on the DrawTo's hiddenLayers set.
   *
   * Element layer values are encoded as (layerNum * 65536 + purposeNum).
   * We decode to extract the raw layer number for visibility checks.
   *
   * @param encodedLayer - Encoded layer value from element data
   * @param to - The DrawTo context carrying the hiddenLayers set
   * @returns True if the layer should be rendered, false if hidden
   */
  private isLayerVisible(encodedLayer: number, to: DrawTo): boolean {
    if (to.hiddenLayers.size === 0) return true;
    // Decode the encoded layer to get raw layer number
    const { layer: layerNum } = pdk_utils.decodeLayer(encodedLayer);
    return !to.hiddenLayers.has(layerNum);
  }

  override redraw = (to: DrawTo): unknown => {
    // Top-level instance visibility — when off, skip drawing the entire
    // instance tree (matches the parent "Instances" toggle in the LayerPanel).
    if (to.layoutDisplay?.instances ?? true) {
      to.layer(this.instances.redraw); // Draw our instances
    }
    // Routes are drawn as overlays outside sprites (see ScalarInstanceEntity.redraw
    // and LayoutContent.redraw) so they remain visible at all zoom levels.
    // Path elements are also drawn as overlays outside sprites (same sites)
    // so they remain visible at all zoom levels with a minimum 1-pixel width.
    return to.layer(this.redrawElements);
  };

  // Draw non-path elements. Paths are drawn separately as overlays outside sprites.
  //
  // Called from `LayoutEntity.redraw` at the **root** (to.viewport is the main
  // canvas viewport in world coords, elements are in world coords) and inside
  // **vector-rendered instances** (to.viewport is the nestedViewport in the
  // cell's local coords, elements are in local coords). In both cases the two
  // coordinate systems match, so `to.viewport` can be used directly for the
  // per-element viewport check.
  //
  // Inside a **sprite** rasterization, `to.viewport` is null (the sprite
  // has an infinite cullViewport so it can be reused from different
  // positions). In that case the viewport check is skipped and only the
  // size cull runs — which is correct, because every element must be baked
  // into the sprite regardless of where it's currently visible.
  private redrawElements = (to: DrawTo): unknown => {
    const { pdk } = to;

    // Compute the world-coordinate size below which elements are culled.
    // to.scale is the schematic-to-page scale factor from the DrawTo context.
    const minCullWorld = to.scale > 0 ? MIN_CULL_SCREEN_PX / to.scale : 0;

    const viewport = to.viewport;

    this.data.elems.forEach((elem) => {
      // Skip path elements -- they are drawn as overlays outside sprites
      if (elem.kind === "path") return;
      // Skip drawing if layer is hidden
      if (!this.isLayerVisible(elem.layer, to)) {
        return;
      }
      const elemBbox = g.LayeredElement.bbox(elem);
      // Viewport cull: if the element's bbox is entirely off-screen, skip.
      // This is the biggest win for zoomed-in scenes where most of the cell
      // is outside the visible area. Null viewport (inside sprites) skips
      // this check so the sprite still rasterizes all content.
      if (viewport && !Bbox.intersects(elemBbox, viewport)) {
        to.elementCulled();
        return;
      }
      // Cull elements smaller than MIN_CULL_SCREEN_PX in both dimensions
      if (minCullWorld > 0) {
        const elemSize = Bbox.size(elemBbox);
        if (elemSize.width < minCullWorld && elemSize.height < minCullWorld) {
          to.elementSizeCulled();
          return;
        }
      }
      // Resolve the layer color once and reuse it for both the element style
      // and the via cross (instead of doing two `pdk.layers.find()` lookups).
      const color = getLayerColor(elem.layer, pdk);
      to.element(elem, styleFromColor(color), this);

      // Draw cross (X) pattern on via layer elements
      if (elem.kind !== "text") {
        const { layer: layerNum } = pdk_utils.decodeLayer(elem.layer);
        if (isViaLayer(layerNum, pdk)) {
          drawViaCross(to, elemBbox, color);
        }
      }
    });
    return null;
  };

  // Draw path elements as overlays outside sprites, with minimum 1-pixel width
  // enforcement. Called from ScalarInstanceEntity.redraw and LayoutContent.redraw.
  // If `localViewport` is provided (the DrawTo viewport inverse-transformed into
  // this cell's local coordinates), path elements entirely outside it are
  // viewport-culled before drawing.
  redrawPathElements = (
    to: DrawTo,
    localViewport: Bbox | null = null
  ): unknown => {
    const { pdk } = to;

    // 1 pixel in world coordinates, for minimum-width enforcement
    const minWorld = to.scale > 0 ? 1 / to.scale : 0;
    // Culling threshold: MIN_CULL_SCREEN_PX pixels in world coordinates
    const minCullWorld = minWorld > 0 ? MIN_CULL_SCREEN_PX * minWorld : 0;

    this.data.elems.forEach((elem) => {
      if (elem.kind !== "path") return;
      // Skip drawing if layer is hidden
      if (!this.isLayerVisible(elem.layer, to)) {
        return;
      }
      const elemBbox = g.LayeredElement.bbox(elem);
      // Viewport cull: if this element is entirely off-screen in the cell's
      // local coordinates, skip drawing. The parent instance's viewport check
      // already eliminated the common case; this trims individual elements
      // when zoomed into part of a big cell.
      if (localViewport && !Bbox.intersects(elemBbox, localViewport)) {
        to.overlayCulled();
        return;
      }
      // Cull path elements smaller than MIN_CULL_SCREEN_PX in both dimensions
      if (minCullWorld > 0) {
        const elemSize = Bbox.size(elemBbox);
        if (elemSize.width < minCullWorld && elemSize.height < minCullWorld) {
          to.overlaySizeCulled();
          return;
        }
      }
      // `to.path()` derives its own line width from the element's real
      // `style.width` (never thinner than 1 screen pixel), not from
      // `s.shape.linewidth` — that generic per-layer default is meaningless
      // for a path's actual wire width.
      to.path(elem, layerStyle(elem.layer, pdk).shape);
    });
    return null;
  };

  // Find instance by name (e.g., "M1")
  readonly findInstanceByName = (name: string): InstanceEntity | undefined => {
    for (const inst of this.instances.values()) {
      if (inst.data.name === name) return inst;
    }
    return undefined;
  };

  // Find all routes for a given net name
  readonly findRoutesByNet = (netName: string): RouteEntity[] => {
    const result: RouteEntity[] = [];
    for (const route of this.routes.values()) {
      if (route.data.net === netName) result.push(route);
    }
    return result;
  };

  toData = (): layout.Impl => ({
    elems: this.data.elems,
    conns: this.data.conns,
    instances: Array.from(this.instances.values()).map((inst) => inst.toData()),
    routes: Array.from(this.routes.values()).map((route) => route.toData()), // => route.data
  });
}
